import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, ArrowLeft, CheckCircle, Quotes, 
  ChartLineUp, Clock, Users, MapPin
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { CASE_STUDIES } from '../data/contentData';

export default function ClientSuccessPage() {
  const { caseId } = useParams();

  // If viewing a specific case study
  if (caseId) {
    const study = CASE_STUDIES.find(c => c.id === caseId);
    if (!study) return <CaseNotFound />;
    return <CaseStudyDetail study={study} />;
  }

  return (
    <div className="min-h-screen transition-colors duration-300" style={{ background: "var(--background)", color: "var(--foreground)" }}>
      <Navbar />

      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00FF94]/5 via-transparent to-transparent" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <span className="text-xs font-mono uppercase tracking-widest text-[#00FF94] mb-4 block">
              Client Success Stories
            </span>
            <h1 className="font-heading text-5xl md:text-7xl font-black tracking-tight mb-6">
              Real Results.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00F0FF] to-[#00FF94]">
                Real Freedom.
              </span>
            </h1>
            <p className="text-xl theme-text-secondary max-w-2xl mx-auto">
              See how founders and CTOs reclaimed their time by outsourcing development to XceedAgents.
            </p>
          </motion.div>

          {/* Stats Banner */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            {[
              { value: '500+', label: 'Projects Delivered' },
              { value: '60%', label: 'Avg. Time Saved' },
              { value: '98%', label: 'Client Satisfaction' },
              { value: '150+', label: 'Global Clients' },
            ].map((stat, i) => (
              <div key={i} className="text-center p-6 border theme-border theme-surface-el">
                <div className="text-4xl font-bold text-[#00FF94] mb-2">{stat.value}</div>
                <div className="text-sm theme-text-muted">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Case Studies Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {CASE_STUDIES.map((study, i) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Link
                  to={`/client-success/${study.id}`}
                  className="group block border theme-border theme-surface-el p-8 hover:border-[#00FF94]/50 transition-all h-full"
                >
                  {/* Header */}
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-xs font-mono uppercase tracking-wider px-3 py-1 bg-[#00FF94]/20 text-[#00FF94]">
                      {study.industry}
                    </span>
                    <span className="text-xs theme-text-muted flex items-center gap-1">
                      <MapPin size={12} /> {study.location}
                    </span>
                  </div>

                  {/* Title */}
                  <h2 className="font-heading text-2xl font-bold mb-4 group-hover:text-[#00FF94] transition-colors">
                    {study.title}
                  </h2>

                  {/* Challenge */}
                  <p className="theme-text-muted text-sm mb-6">
                    {study.challenge.substring(0, 150)}...
                  </p>

                  {/* Key Results */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {study.results.slice(0, 2).map((result, j) => (
                      <div key={j} className="text-center p-3 bg-[var(--input-bg)]">
                        <div className="text-2xl font-bold text-[#00F0FF]">{result.value}</div>
                        <div className="text-xs theme-text-muted">{result.metric}</div>
                      </div>
                    ))}
                  </div>

                  {/* Testimonial Preview */}
                  <div className="flex items-start gap-3 p-4 bg-[var(--input-bg)] mb-6">
                    <Quotes size={20} className="text-[#00FF94] flex-shrink-0" />
                    <p className="text-sm theme-text-secondary italic">
                      "{study.testimonial.quote.substring(0, 100)}..."
                    </p>
                  </div>

                  {/* CTA */}
                  <div className="flex items-center gap-2 text-[#00FF94] text-sm font-mono">
                    Read Full Story <ArrowRight size={16} />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 theme-surface">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-4xl font-bold mb-6">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-xl theme-text-secondary mb-10">
            Join 150+ founders who've reclaimed their time with XceedAgents.
          </p>
          <Link to="/start-project">
            <Button 
              size="lg"
              className="bg-[#00FF94] text-black font-bold hover:shadow-[0_0_30px_rgba(0,255,148,0.5)] transition-all uppercase tracking-wide h-14 px-8"
            >
              Start Your Project <ArrowRight size={20} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// Case Study Detail Page
function CaseStudyDetail({ study }) {
  const otherStudies = CASE_STUDIES.filter(c => c.id !== study.id).slice(0, 2);

  return (
    <div className="min-h-screen transition-colors duration-300" style={{ background: "var(--background)", color: "var(--foreground)" }}>
      <Navbar />

      <article className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6">
          {/* Back Link */}
          <Link 
            to="/client-success" 
            className="inline-flex items-center gap-2 text-[#00FF94] font-mono text-sm mb-8"
          >
            <ArrowLeft size={16} /> Back to Case Studies
          </Link>

          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <span className="text-xs font-mono uppercase tracking-wider px-3 py-1 bg-[#00FF94]/20 text-[#00FF94]">
                {study.industry}
              </span>
              <span className="text-sm theme-text-muted flex items-center gap-2">
                <MapPin size={14} /> {study.location}
              </span>
              <span className="text-sm theme-text-muted flex items-center gap-2">
                <Clock size={14} /> {study.timeline}
              </span>
            </div>

            <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight mb-6">
              {study.title}
            </h1>

            <p className="text-xl theme-text-secondary">
              {study.client}
            </p>
          </header>

          {/* Results Grid */}
          <div className="grid md:grid-cols-4 gap-4 mb-12">
            {study.results.map((result, i) => (
              <div key={i} className="p-6 border border-[#00FF94]/30 bg-[#00FF94]/5 text-center">
                <div className="text-3xl font-bold text-[#00FF94] mb-2">{result.value}</div>
                <div className="text-sm text-white mb-1">{result.metric}</div>
                <div className="text-xs theme-text-muted">{result.comparison}</div>
              </div>
            ))}
          </div>

          {/* Challenge */}
          <section className="mb-12">
            <h2 className="font-heading text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#FF6B00]/20 flex items-center justify-center">
                <ChartLineUp size={18} className="text-[#FF6B00]" />
              </span>
              The Challenge
            </h2>
            <p className="theme-text-secondary text-lg leading-relaxed">
              {study.challenge}
            </p>
          </section>

          {/* Solution */}
          <section className="mb-12">
            <h2 className="font-heading text-2xl font-bold mb-4 flex items-center gap-3">
              <span className="w-8 h-8 bg-[#00F0FF]/20 flex items-center justify-center">
                <CheckCircle size={18} className="text-[#00F0FF]" />
              </span>
              Our Solution
            </h2>
            <p className="theme-text-secondary text-lg leading-relaxed mb-6">
              {study.solution}
            </p>
            
            {/* Team & Tech */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 border theme-border theme-surface-el">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Users size={18} className="text-[#00F0FF]" /> Team Composition
                </h3>
                <p className="theme-text-secondary">{study.teamSize}</p>
              </div>
              <div className="p-6 border theme-border theme-surface-el">
                <h3 className="font-bold mb-4">Tech Stack</h3>
                <div className="flex flex-wrap gap-2">
                  {study.techStack.map((tech, i) => (
                    <span key={i} className="text-xs px-2 py-1 bg-white/10 text-slate-300">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Testimonial */}
          <section className="mb-12 p-8 border border-[#00FF94]/30 bg-[#00FF94]/5">
            <Quotes size={32} className="text-[#00FF94] mb-4" />
            <blockquote className="text-2xl font-heading italic mb-6">
              "{study.testimonial.quote}"
            </blockquote>
            <cite className="theme-text-secondary not-italic">
              — {study.testimonial.author}
            </cite>
          </section>

          {/* CTA */}
          <div className="p-8 border border-[#00F0FF]/30 bg-[#00F0FF]/5 text-center">
            <h3 className="font-heading text-2xl font-bold mb-4">
              Want Similar Results?
            </h3>
            <p className="theme-text-secondary mb-6">
              Let's discuss how we can help you reclaim your time and accelerate growth.
            </p>
            <Link to="/start-project">
              <Button className="bg-[#00F0FF] text-black font-bold px-8">
                Start Your Project <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Other Case Studies */}
        <div className="max-w-7xl mx-auto px-6 mt-24">
          <h2 className="font-heading text-2xl font-bold mb-8">More Success Stories</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {otherStudies.map(otherStudy => (
              <Link
                key={otherStudy.id}
                to={`/client-success/${otherStudy.id}`}
                className="group border theme-border theme-surface-el p-6 hover:border-[#00FF94]/50 transition-all"
              >
                <span className="text-xs font-mono text-[#00FF94]">{otherStudy.industry}</span>
                <h3 className="font-bold text-xl mt-2 mb-2 group-hover:text-[#00FF94] transition-colors">
                  {otherStudy.title}
                </h3>
                <p className="text-sm theme-text-muted">{otherStudy.client} • {otherStudy.location}</p>
              </Link>
            ))}
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}

function CaseNotFound() {
  return (
    <div className="min-h-screen transition-colors duration-300" style={{ background: "var(--background)", color: "var(--foreground)" }}>
      <Navbar />
      <div className="py-32 text-center">
        <h1 className="font-heading text-4xl font-bold mb-4">Case Study Not Found</h1>
        <p className="theme-text-muted mb-8">The case study you're looking for doesn't exist.</p>
        <Link to="/client-success">
          <Button>Back to Case Studies</Button>
        </Link>
      </div>
      <Footer />
    </div>
  );
}
